/* <poster-video>: a poster <img> that becomes a muted, looping inline video near the viewport.
   Used by the step cards of sections/video-showcase-steps-v2 (phones) and sections/video-showcase-steps.

   Files (data attributes; only data-src is required, so older markup keeps working):
     data-src            the default file (the v2 cards pass the 720p rendition)
     data-src-lite       used on data saver / 2G–3G connections (480p)
     data-src-fallback   used once if the chosen file fails to load (the 1080p file)

   Playback: ONE card per [data-poster-video-group] plays — the most visible one at least PLAY_RATIO in view
   (a tie goes to the earlier card, so the first step plays first). IntersectionObservers only, no scroll
   listener. Near the viewport a card fetches metadata only; the full download starts when it plays. A card the
   visitor paused stays paused until it leaves the viewport. Reduced motion / data saver: no autoplay — tap to play. */
(function () {
  if (customElements.get('poster-video')) return;

  const PRELOAD_MARGIN = '200px 0px'; // create the player (metadata only) a little before the card is visible
  const PLAY_RATIO = 0.6; // a card must be at least this visible to play
  const THRESHOLDS = [0, 0.25, 0.5, 0.6, 0.75, 0.9, 1];

  const conn = navigator.connection;
  const SAVE_DATA = !!(conn && conn.saveData);
  const SLOW = !!(conn && /(^|-)2g|3g/.test(conn.effectiveType || ''));
  const AUTOPLAY_OK = !(window.matchMedia('(prefers-reduced-motion: reduce)').matches || SAVE_DATA);

  // All live instances — the group decision looks across them.
  const instances = new Set();

  // The card that should play in a group: the most visible one past PLAY_RATIO, earlier card on a tie.
  function pick(group) {
    // a card the visitor tapped to play owns the group while it is on screen
    for (const inst of instances) {
      if (inst.group === group && inst._manual && inst._ratio > 0) {
        for (const other of instances) if (other !== inst && other.group === group) other.video?.pause();
        return;
      }
    }
    let best = null;
    for (const inst of instances) {
      if (inst.group !== group || inst._ratio < PLAY_RATIO || inst._userPaused) continue;
      if (
        !best ||
        inst._ratio > best._ratio + 0.01 ||
        (Math.abs(inst._ratio - best._ratio) <= 0.01 && best.compareDocumentPosition(inst) & Node.DOCUMENT_POSITION_PRECEDING)
      ) best = inst;
    }
    for (const inst of instances) {
      if (inst.group === group && inst !== best && !inst._manual) inst.video?.pause();
    }
    if (best && AUTOPLAY_OK) best.play();
  }

  class PosterVideo extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.src) return;
      this.video = null;
      this._ratio = 0;
      this._userPaused = false;
      this._manual = false;
      if (this.getAttribute('role') === 'button') this.setAttribute('aria-pressed', 'false');
      instances.add(this);

      this._ac = new AbortController();
      const { signal } = this._ac;

      // Tap / click / Enter / Space toggles; a manual pause sticks until the card leaves the viewport.
      const toggle = () => {
        const video = this.ensureVideo();
        if (video.paused) {
          this._userPaused = false;
          this._manual = true; // a tapped card keeps playing even if another card is a bit more visible
          for (const other of instances) if (other !== this && other.group === this.group) { other._manual = false; other.video?.pause(); }
          this.play();
        } else {
          this._userPaused = true;
          this._manual = false;
          video.pause();
        }
      };
      this.addEventListener('click', toggle, { signal });
      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
        e.preventDefault();
        toggle();
      }, { signal });

      // Warm-up: create the player (metadata only) shortly before the card is visible, then stop watching.
      this.preloadIO = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting) || !AUTOPLAY_OK) return;
        this.ensureVideo();
        this.preloadIO.disconnect();
      }, { rootMargin: PRELOAD_MARGIN });
      this.preloadIO.observe(this);

      // Visibility: record how much of the card is in view and let the group decide who plays.
      this.playIO = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          this._ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
          if (this._ratio === 0) { this._userPaused = false; this._manual = false; this.video?.pause(); }
        }
        pick(this.group);
      }, { threshold: THRESHOLDS });
      this.playIO.observe(this);
    }

    disconnectedCallback() {
      this.preloadIO?.disconnect();
      this.playIO?.disconnect();
      this._ac?.abort();
      instances.delete(this);
      // Release the player so a re-connect (editor re-render) starts clean — no duplicate <video>.
      const video = this.video;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
        video.remove();
        this.video = null;
      }
      this.classList.remove('is-playing');
    }

    // Resolved lazily: the group attribute may be set after upgrade (e.g. per card on wide screens).
    get group() {
      return this.closest('[data-poster-video-group]');
    }

    play() {
      const video = this.ensureVideo();
      video.preload = 'auto'; // the full download starts only for the card that plays
      if (!video.paused) return;
      const result = video.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }

    ensureVideo() {
      if (this.video) return this.video;

      const video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.setAttribute('aria-hidden', 'true'); // decorative: the host is the labelled button
      video.loop = this.dataset.loop !== 'false';
      video.preload = 'metadata';
      video.className = this.dataset.videoClass || '';
      video.src = ((SAVE_DATA || SLOW) && this.dataset.srcLite) || this.dataset.src;

      // A missing rendition → the fallback file, once.
      const fallback = this.dataset.srcFallback;
      if (fallback) {
        video.addEventListener('error', () => {
          if (video.dataset.fellBack) return;
          video.dataset.fellBack = '1';
          video.src = fallback;
          // resume through the group rule (one card plays), or this card if the visitor tapped it
          if (this._manual) this.play(); else pick(this.group);
        }, { signal: this._ac.signal });
      }

      // state for styling and assistive tech: the host is a toggle button (aria-pressed = playing)
      video.addEventListener('playing', () => { this.classList.add('is-playing'); this.setAttribute('aria-pressed', 'true'); }, { signal: this._ac.signal });
      video.addEventListener('pause', () => { this.classList.remove('is-playing'); this.setAttribute('aria-pressed', 'false'); }, { signal: this._ac.signal });

      // Right above the poster in the DOM, below the absolutely positioned overlays (they carry their own z-index).
      const poster = this.querySelector('img');
      if (poster) poster.after(video);
      else this.prepend(video);

      this.video = video;
      return video;
    }
  }

  customElements.define('poster-video', PosterVideo);
})();
